import React from 'react';
import { Edit2, Trash2, BookOpen } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../ui/utils';

interface GrammarCategory {
  name: string;
  display_name: string;
  description?: string;
  metadata_exists?: boolean;
  metadata_pending?: boolean;
  linguistic_type?: string;
  business_relevance?: {
    importance?: string;
  };
  subcategory_count?: number;
  complexity_range?: {
    average?: number;
  };
  cefr_coverage?: {
    core_level?: string;
  };
  features?: Array<any>;
  featureCount?: number;
}

interface CategorySidebarProps {
  categories: GrammarCategory[];
  selectedCategory: GrammarCategory | null;
  onCategorySelect: (category: GrammarCategory) => void;
  onCategoryEdit: (category: GrammarCategory) => void;
  onCategoryDelete: (categoryName: string) => void;
}

export function CategorySidebar({
  categories,
  selectedCategory,
  onCategorySelect,
  onCategoryEdit,
  onCategoryDelete,
}: CategorySidebarProps) {
  const getComplexityColor = (average?: number) => {
    if (!average) return 'bg-gray-100 text-gray-800';
    if (average <= 3) return 'bg-green-100 text-green-800';
    if (average <= 6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getImportanceColor = (importance?: string) => {
    switch (importance) {
      case 'essential': return 'bg-purple-100 text-purple-800';
      case 'high': return 'bg-blue-100 text-blue-800';
      case 'medium': return 'bg-teal-100 text-teal-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-sidebar-border">
        <h2 className="font-semibold text-sidebar-foreground">Categories ({categories.length})</h2>
      </div>
      
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="p-2 space-y-2">
          {categories.map((category) => (
            <div
              key={category.name}
              className={cn(
                "group p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md",
                selectedCategory?.name === category.name
                  ? "bg-sidebar-accent border-sidebar-primary ring-1 ring-sidebar-primary"
                  : "bg-card border-sidebar-border hover:bg-sidebar-accent/50"
              )}
              onClick={() => onCategorySelect(category)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-purple-600" />
                  <h3 className="font-semibold text-card-foreground">{category.display_name}</h3>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCategoryEdit(category);
                    }}
                  >
                    <Edit2 className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCategoryDelete(category.name);
                    }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                {category.description || "No description available"}
              </p>

              <div className="flex flex-wrap gap-2 mb-3">
                {category.complexity_range?.average !== undefined && (
                  <Badge 
                    variant="secondary" 
                    className={getComplexityColor(category.complexity_range.average)}
                  >
                    Complexity: {category.complexity_range.average}
                  </Badge>
                )}
                {category.business_relevance?.importance && (
                  <Badge 
                    variant="secondary"
                    className={getImportanceColor(category.business_relevance.importance)}
                  >
                    {category.business_relevance.importance}
                  </Badge>
                )}
                {category.cefr_coverage?.core_level && (
                  <Badge variant="outline" className="text-xs">
                    {category.cefr_coverage.core_level}
                  </Badge>
                )}
                {category.metadata_pending && (
                  <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200">
                    Metadata pending
                  </Badge>
                )}
              </div>

              <div className="flex items-center justify-end text-xs text-muted-foreground">
                <span>{category.featureCount || 0} features</span>
              </div>
            </div>
          ))}
          {/* Spacer to ensure last item is scrollable */}
          <div className="h-32" />
        </div>
      </div>
    </div>
  );
}